import * as React from 'react';
import { useEffect, useMemo, useState } from "react";
import { Box, Button, Card, CardContent, CardMedia, Grid, TablePagination, Typography, TextField, InputAdornment, IconButton, Stack, Snackbar, Alert } from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import { Link as RouterLink } from "react-router-dom";
import GoogleAd from "../../Components/GoogleAd";
import { AD_CONFIG } from "../../config/ads";
import { toggleLikedAlbum, getLikedAlbums } from "../../utils/likeStorage";
import { useAlbums, useLikeAlbum } from "../../hooks/useAlbums";
import Seo from "../../Components/Seo";
import ResourceLearningSummary from "../../Components/ResourceLearningSummary";
import SponsorAd from "../../Components/SponsorAd";


const InfoGraphics = () => {
    const { data, isLoading: loading } = useAlbums('all', 1, 1000);
    const albums = useMemo(() => data?.albums || [], [data?.albums]);
    const { mutateAsync: toggleLikeMutation } = useLikeAlbum();
    
    const [currentPage, setCurrentPage] = useState(0); // MUI uses 0-based index
    const [pageSize, setPageSize] = useState(50);
    const [searchTerm, setSearchTerm] = useState('');
    const [toast, setToast] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });
    const [likedAlbums, setLikedAlbums] = useState<Set<number>>(getLikedAlbums());

    const filterInfoGraphics = useMemo(() => {
        if (!albums || albums.length === 0) return [];
        const filtered = albums
            .filter((album) => album.category === "RadioInfo")
            .filter((album) => {
                if (!searchTerm) return true;
                const searchLower = searchTerm.toLowerCase();
                return album.title.toLowerCase().includes(searchLower) ||
                    (album.description && album.description.toLowerCase().includes(searchLower));
            })
            .map((album) => ({
                id: album.id,
                title: album.title,
                description: album.description,
                coverImage: album.coverImage,
                uploadedBy: album.uploadedBy,
                category: album.category,
                like: album.like === null || album.like === undefined ? 0 : album.like,
            }))
            .sort((a, b) => a.title.localeCompare(b.title)); // Sort by title alphabetically

        // Apply client-side pagination
        const startIndex = currentPage * pageSize;
        const endIndex = startIndex + pageSize;
        return filtered.slice(startIndex, endIndex);
    }, [albums, currentPage, pageSize, searchTerm])

    const totalFilteredCount = useMemo(() => {
        if (!albums || albums.length === 0) return 0;
        return albums
            .filter((album) => album.category === "RadioInfo")
            .filter((album) => {
                if (!searchTerm) return true;
                const searchLower = searchTerm.toLowerCase();
                return album.title.toLowerCase().includes(searchLower) ||
                    (album.description && album.description.toLowerCase().includes(searchLower));
            }).length;
    }, [albums, searchTerm]);

    // Reset to first page when search term changes
    useEffect(() => {
        setCurrentPage(0);
    }, [searchTerm]);

    const handleChangePage = (_: unknown, newPage: number) => {
        setCurrentPage(newPage);
    };

    const handleLike = async (albumId: number, currentLikes: number) => {
        try {
            // Check if already liked
            const isLiked = likedAlbums.has(albumId);
            const action = isLiked ? 'unlike' : 'like';

            // Call API to toggle like via React Query mutation
            await toggleLikeMutation({ id: albumId, action });

            // Update localStorage
            toggleLikedAlbum(albumId);

            // Update local state for immediate UI update
            const newLikedAlbums = new Set(likedAlbums);
            if (isLiked) {
                newLikedAlbums.delete(albumId);
            } else {
                newLikedAlbums.add(albumId);
            }
            setLikedAlbums(newLikedAlbums);

            // Show success message
            setToast({
                open: true,
                message: action === 'like' ? 'Album liked!' : 'Album unliked!',
                severity: 'success'
            });
        } catch (error: any) {
            console.error('Error toggling like:', error);
            setToast({ open: true, message: error?.message || 'Failed to update like. Please try again.', severity: 'error' });
        }
    };

    const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newPageSize = parseInt(e.target.value, 10);
        setPageSize(newPageSize);
        setCurrentPage(0); // Reset to first page when page size changes
    };


    return (
        <Box sx={{ p: { xs: 1, sm: 2 } }}>
            <Seo
                title="Radiology Info-Graphics"
                description="Browse educational radiology infographics that summarize diagnostic signs, disease classifications, staging systems, and quick-reference imaging concepts."
                canonicalPath="/infographics"
            />
            {loading ? <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Loading...</Typography> : null}
            <Typography
                variant="h1"
                color="textSecondary"
                align="center"
                sx={{
                    fontSize: {
                        xs: '1.75rem',
                        sm: '2.5rem',
                        md: '3rem',
                    },
                    fontWeight: 700,
                    mb: { xs: 1, sm: 2 },
                }}
            >
                Info Graphics
            </Typography>
            <Typography
                variant="body1"
                color="text.secondary"
                align="center"
                sx={{
                    mb: { xs: 3, sm: 4 },
                    maxWidth: '900px',
                    mx: 'auto',
                    fontSize: { xs: '1rem', sm: '1.125rem' },
                    lineHeight: 1.8
                }}
            >
                Discover comprehensive radiology infographics that condense extensive medical data into easy-to-digest visuals. Our infographics cover a wide range of topics, from disease classifications and staging systems to key diagnostic checklists, making them an essential quick-reference tool for daily clinical practice and exam preparation.
            </Typography>
            <ResourceLearningSummary variant="infographics" />
            <SponsorAd my={{ xs: 2.5, sm: 3.5 }} placement="infographics_top" />
            <Box sx={{ mb: 3 }}>
                <TextField
                    fullWidth
                    placeholder="Search albums by title or description..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                        },
                    }}
                />
            </Box>
            {/* Ad after search bar - Only show if we have content */}
            {filterInfoGraphics.length > 0 && (
                <Box sx={{ mb: 3 }}>
                    <GoogleAd
                        adSlot={AD_CONFIG.adUnits.albumPageTop}
                        format="horizontal"
                    />
                </Box>
            )}
            <Grid container spacing={2}  >
                {filterInfoGraphics.map((album, index) => {
                    return (
                        <React.Fragment key={album.id}>
                            <Grid size={{ lg: 4, xs: 12, md: 4 }} display="flex">
                                <Card sx={{ borderRadius: 3, width: '100%', display: 'flex', flexDirection: 'column' }}>
                                <CardMedia
                                    component="img"
                                    image={album.coverImage}
                                    alt={album.title}
                                    sx={{ height: { xs: 200, sm: 250, md: 300 }, objectFit: 'cover' }}
                                    loading="lazy"
                                />
                                <CardContent sx={{ flexGrow: 1, p: { xs: 1.5, sm: 2 } }}>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: {
                                                xs: '1rem',
                                                sm: '1.25rem',
                                            },
                                            lineHeight: 1.3,
                                            mb: 1,
                                        }}
                                    >
                                        {album.title}
                                    </Typography>
                                    {album.description && (
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                mt: 0.5,
                                                opacity: 0.8,
                                                fontSize: {
                                                    xs: '0.8rem',
                                                    sm: '0.875rem',
                                                },
                                                lineHeight: 1.5,
                                            }}
                                        >
                                            {album.description}
                                        </Typography>
                                    )}
                                </CardContent>
                                <Box sx={{ p: { xs: 1.5, sm: 2 }, pt: 0 }}>
                                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                        <Button
                                            variant="outlined"
                                            component={RouterLink}
                                            to={`/album/${album.id}`}
                                            size="small"
                                            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                                        >
                                            More images
                                        </Button>
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleLike(album.id, album.like || 0)}
                                                sx={{ color: likedAlbums.has(album.id) ? 'error.main' : 'action.disabled' }}
                                            >
                                                {likedAlbums.has(album.id) ? (
                                                    <FavoriteIcon fontSize="small" />
                                                ) : (
                                                    <FavoriteBorderIcon fontSize="small" />
                                                )}
                                            </IconButton>
                                            <Typography variant="body2" color="text.secondary" sx={{ minWidth: '20px', textAlign: 'center' }}>
                                                {typeof album.like === 'number' ? album.like : 0}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                </Box>
                                </Card>
                            </Grid>
                            {index === 5 && filterInfoGraphics.length > 6 && (
                                <Grid size={{ xs: 12 }}>
                                    <SponsorAd my={{ xs: 1, sm: 2 }} placement="infographics_grid" />
                                </Grid>
                            )}
                        </React.Fragment>
                    )
                })}
            </Grid>

            {/* Ad before pagination */}
            {filterInfoGraphics.length > 0 && (
                <Box sx={{ mt: 3, mb: 2 }}>
                    <GoogleAd
                        adSlot={AD_CONFIG.adUnits.albumPageBottom}
                        format="horizontal"
                    />
                </Box>
            )}

            <TablePagination
                component="div"
                count={totalFilteredCount} // total filtered RadioInfo albums
                page={currentPage} // 0-based
                onPageChange={handleChangePage}
                rowsPerPage={pageSize}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />

            <Snackbar
                open={toast.open}
                autoHideDuration={3000}
                onClose={() => setToast({ ...toast, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setToast({ ...toast, open: false })} severity={toast.severity} sx={{ width: '100%' }}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default InfoGraphics;
